// Who sees EVERY pilot's records, and who only their own (Mustang H178).
//
// Aaron 2026-09-29: "pilots see their own records - flight admin sees all
// pilots", and then "Yes, same rule everywhere". A SCOPED PILOT is a member of
// the tenant's Pilots group (crewGroup.ts, H172) who is not a flight admin
// (useIsFlightAdmin.ts, the view_all_pilots token). Everyone else — flight
// admins, and office staff who are not pilots — sees every pilot. It is the
// server's rule, aeronet-fast-api app/flightduty/access.py `is_scoped_pilot` /
// `sees_all_records`, which every pilot scope (F&D, pilot roster, timesheets,
// flights, flight plans, expenses, load sheets, the H17 pilot pickers) reads.
//
// The flight-admin test on its own still decides what is the FLIGHT ADMIN'S,
// not a scope: the Rules mode (RulesGate) and timesheet review.
import { useMemo } from "react";

import { useConfig } from "../hooks/useConfig";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { isInCrewGroupFor } from "./crewGroup";
import { isFlightAdminFor } from "./rules/useIsFlightAdmin";

type Bag = { [key: string]: any } | undefined | null;

/** May this user reach every pilot's records? Everyone but a scoped pilot. */
export function seesAllPilotsFor(currentUser: Bag, dutyConfig: Bag): boolean {
  if (!currentUser) return false;
  return isFlightAdminFor(currentUser, dutyConfig) || !isInCrewGroupFor(currentUser, dutyConfig);
}

/** THE rule's other face: this user reaches only their own records. */
export function isScopedPilotFor(currentUser: Bag, dutyConfig: Bag): boolean {
  return !!currentUser && !seesAllPilotsFor(currentUser, dutyConfig);
}

export interface PilotScopeState {
  /** `false` while loading: unknown is "own only", so nothing flashes. */
  seesAllPilots: boolean;
  loading: boolean;
}

/** `useIsFlightAdmin`'s loading rule: withheld until the user AND the
 *  `flightduty` config have resolved (the config names the Pilots group and the
 *  token, and a missing config would read as "unconfigured ⇒ yes"). */
export function useSeesAllPilots(): PilotScopeState {
  const currentUser = useCurrentUser();
  const dutyConfig = useConfig("flightduty");
  return useMemo(() => {
    const loading = currentUser == null || dutyConfig === undefined;
    return {
      loading,
      seesAllPilots: loading ? false : seesAllPilotsFor(currentUser, dutyConfig),
    };
  }, [currentUser, dutyConfig]);
}
